"use client";

import { KeyedMutator } from "swr";

import { IInvoice, SubscriptionInvoice } from "@/backend/domains/invoice/domain/Invoice";
import { IPaymentPlan } from "@/backend/domains/subscription/domain/PaymentPlan";
import { ISubscription } from "@/backend/domains/subscription/domain/Subscription";

import { execAction } from "@/lib/executeServerAction";
import { useBaseServerAction } from "@/hooks/useServerAction";
import { BaseStoreProps, createAdvancedStore } from "@/stores/createAdvancedStore";
import { clientApi } from "@/actions";

import { useOrganizationStore } from "./organization.store";

//

export interface SubscriptionStore extends BaseStoreProps {
  subscription: ISubscription;
  mutateSubscription: KeyedMutator<ISubscription>;
  mutateSubscriptionData: KeyedMutator<
    | {
        invoice: IInvoice<SubscriptionInvoice>;
        paymentPlan: IPaymentPlan;
      }
    | undefined
  >;
  invoice: IInvoice<SubscriptionInvoice>;
  paymentPlan: IPaymentPlan;
}

export const subscriptionStore = createAdvancedStore<SubscriptionStore>("Subscription");

export const useSubscriptionStore = () => {
  const subscription = subscriptionStore.useContext((s) => s.subscription);
  const invoice = subscriptionStore.useContext((s) => s.invoice);
  const paymentPlan = subscriptionStore.useContext((s) => s.paymentPlan);
  return { ...subscription, invoice, paymentPlan };
};

export const useMutateSubscription = () => {
  const mutate = subscriptionStore.useContext((s) => s.mutateSubscription);
  return mutate;
};

//

export function useGetSubscriptionStoreData(): SubscriptionStore | undefined {
  const { id: organizationId } = useOrganizationStore();

  const { data: subscription, mutate: mutateSubscription } = useBaseServerAction(
    { key: "subscription", organizationId },
    clientApi.subscription.getSubscription,
  );

  const { invoiceId, planId, id: subscriptionId } = subscription ?? {};

  const { data, mutate: mutateSubscriptionData } = useBaseServerAction(
    { key: "subscription-data", organizationId, subscriptionId, invoiceId, planId },
    async () => {
      if (!subscription) return undefined;
      const { invoiceId, planId } = subscription;

      const invoiceAction = () => clientApi.invoice.getSubscriptionInvoiceById(invoiceId);
      const invoicePromise = execAction(invoiceAction);

      const paymentPlanAction = () => clientApi.paymentPlan.getPaymentPlanByType(planId);
      const paymentPlanPromise = execAction(paymentPlanAction);

      const [invoice, paymentPlan] = await Promise.all([invoicePromise, paymentPlanPromise]);

      return { data: { invoice, paymentPlan } };
    },
  );

  if (!subscription || !data) return;

  return { subscription, mutateSubscription, mutateSubscriptionData, ...data };
}
